"use client";

import React, { useEffect } from "react";
import { useTranslations } from "@/lib/i18n-client";
import { track } from "@/lib/analytics";
import { Reveal, Stagger, StaggerItem } from "@/components/shared/motion";
import { Search, QrCode, Plane } from "lucide-react";

/** How it works as boarding-pass steps (check-in flow). Anchor target #how.
 *  Market-test: wf_airport_boarding_pass_flow_v1 (market-test-plan.md §3 design
 *  system; research/07 CF1 boarding-pass step flow). */
const EXPERIMENT_ID = "wf_airport_boarding_pass_flow_v1";

export default function BoardingSteps() {
  const { t } = useTranslations();

  useEffect(() => {
    try {
      track({
        event_name: "boarding_steps_viewed",
        event_id: `boarding_steps_viewed:${EXPERIMENT_ID}`,
        experiment_id: EXPERIMENT_ID,
      });
    } catch {
      /* analytics is best-effort */
    }
  }, []);

  const steps = [
    { tag: t("sky.steps.1.tag", "STEP 01"), icon: Search, title: t("sky.steps.1.title", "Pick your destination"), desc: t("sky.steps.1.desc", "Search where you're flying and choose a plan that fits your trip.") },
    { tag: t("sky.steps.2.tag", "STEP 02"), icon: QrCode, title: t("sky.steps.2.title", "Install before you board"), desc: t("sky.steps.2.desc", "Get your QR code by email and set up the eSIM while you're still on Wi-Fi.") },
    { tag: t("sky.steps.3.tag", "STEP 03"), icon: Plane, title: t("sky.steps.3.title", "Land connected"), desc: t("sky.steps.3.desc", "Switch on data when you land — no SIM line, no airport Wi-Fi needed.") },
  ];

  return (
    <section id="how" className="sky-wash border-y border-border">
      <div className="container mx-auto px-4 py-16">
        <Reveal as="div" y={16} className="text-center max-w-2xl mx-auto">
          <h2 className="text-2xl sm:text-3xl text-foreground">{t("sky.steps.heading", "Connected in three steps")}</h2>
          <p className="text-muted-foreground mt-2">{t("sky.steps.sub", "A check-in flow built for travelers, not telecom.")}</p>
        </Reveal>
        <Stagger as="div" staggerChildren={0.08} amount={0.25} className="mt-10 grid md:grid-cols-3 gap-5">
          {steps.map(({ tag, icon: Icon, title, desc }, i) => (
            <StaggerItem key={i} as="div" y={14} className="boarding-pass bg-card border border-border shadow-sm transition-all hover:-translate-y-1 hover:shadow-md">
              <div className="flex items-center justify-between px-5 pt-5">
                <span className="text-xs font-bold tracking-widest text-primary">{tag}</span>
                <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-primary/10 text-primary">
                  <Icon className="w-5 h-5" />
                </span>
              </div>
              <div className="px-5 pb-6 pt-3">
                <h3 className="text-lg text-foreground">{title}</h3>
                <p className="text-sm text-muted-foreground mt-1.5">{desc}</p>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>
  );
}
